import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ArrowLeftRight, PackagePlus } from 'lucide-react'
import { createStockMovement, getInventoryItems, listPutawayQueue, markPutawayDone } from '../../api/inventory'
import { listReceiptSkuQueue } from '../../api/purchase'
import ErrorBanner from '../../components/ErrorBanner'
import { useToast } from '../../components/Toast.jsx'
import { fmtDay, localToday } from '../../utils/format'
import { num } from '../sales/salesShared'
import BinNav from './BinNav'
import BinScan from './BinScan'
import ItemPicker from './ItemPicker'
import { binLabel, qty, stockItems, unitCost, useAllAreas, useBins, useItemBins, useLocations } from './inventoryShared'

// Put received goods into bins (from the waiting list), or move stock from
// one bin to another.
export default function BinMoveBySku() {
  const [params] = useSearchParams()
  const { notify } = useToast()
  const locations = useLocations()
  const [mode, setMode] = useState(params.get('mode') === 'move' ? 'move' : 'putaway')
  const [items, setItems] = useState([])
  const [productId, setProductId] = useState(params.get('product_id') || '')
  const allAreas = useAllAreas()
  // A bin chosen by scanning (or from the address) waits here until its
  // location's bins have loaded, so the defaults below don't replace it.
  const wantFrom = useRef(params.get('from_area_id') || '')
  const wantTo = useRef('')
  const [queue, setQueue] = useState(null) // [{ id?, product_id, sku, name, qty, when }]
  const [queueId, setQueueId] = useState(null)
  const [count, setCount] = useState('')
  const [fromAreaId, setFromAreaId] = useState('')
  const [toLoc, setToLoc] = useState('')
  const [toAreaId, setToAreaId] = useState('')
  const [version, setVersion] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { getInventoryItems().then((d) => setItems(stockItems(d))).catch(() => setItems([])) }, [version])
  useEffect(() => { if (locations?.length && !toLoc) setToLoc(String(locations[0].location_id)) }, [locations, toLoc])

  // What's waiting to be put away: the put-away list, then anything just
  // received (from Receiving's "Put away now"), then this week's receipts.
  const loadQueue = async () => {
    const fromList = await listPutawayQueue().catch(() => [])
    const rows = (fromList || []).map((r) => ({ id: r.id, product_id: r.product_id, sku: r.sku, name: r.name, qty: num(r.qty), when: r.created_at }))
    if (!rows.length) {
      const recent = await listReceiptSkuQueue(localToday(), 7).catch(() => null)
      ;(recent?.rows || []).forEach((r) => rows.push({ product_id: r.product_id, sku: r.sku, name: r.name, qty: num(r.qty) }))
    }
    setQueue(rows)
  }
  useEffect(() => { loadQueue() }, [version])

  // From Receiving: ?skus=A,B picks the first of them.
  useEffect(() => {
    const skus = (params.get('skus') || '').split(',').map((s) => s.trim()).filter(Boolean)
    if (!skus.length || !items.length || productId) return
    const first = items.find((p) => skus.includes(p.sku))
    if (first) setProductId(String(first.product_id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  const item = items.find((p) => String(p.product_id) === String(productId))
  const itemBins = useItemBins(item?.sku, version)
  const holding = useMemo(() => (itemBins?.rows || []).filter((r) => num(r.qty) > 0), [itemBins])
  const inBins = holding.reduce((s, r) => s + num(r.qty), 0)
  const loose = Math.max(0, num(item?.qty_on_hand) - inBins)
  const toBins = useBins(toLoc)
  const fromBin = holding.find((r) => String(r.area_id) === String(fromAreaId))
  const toBin = toBins.find((b) => String(b.area_id) === String(toAreaId))

  // Sensible starting choices: the bin holding the most, and the pick bin.
  useEffect(() => {
    const wanted = holding.find((r) => String(r.area_id) === String(wantFrom.current))
    if (wanted) { setFromAreaId(String(wanted.area_id)); wantFrom.current = ''; return }
    setFromAreaId(holding.length ? String([...holding].sort((a, b) => num(b.qty) - num(a.qty))[0].area_id) : '')
  }, [holding])
  useEffect(() => {
    const wanted = toBins.find((b) => String(b.area_id) === String(wantTo.current))
    if (wanted) { setToAreaId(String(wanted.area_id)); wantTo.current = ''; return }
    const pick = itemBins?.pick_path && toBins.find((b) => b.path === itemBins.pick_path)
    setToAreaId(pick ? String(pick.area_id) : '')
  }, [itemBins, toBins])

  const hasBins = allAreas.some((a) => a.level === 'Bin')
  const notABin = (a) => (hasBins && a.level !== 'Bin' ? `That’s ${a.level === 'Aisle' ? 'an' : 'a'} ${a.level.toLowerCase()} label (${a.path}). Scan a bin.` : '')
  const scanTo = (a) => {
    if (String(a.location_id) === String(toLoc)) { setToAreaId(String(a.area_id)); return }
    wantTo.current = String(a.area_id)
    setToLoc(String(a.location_id))
  }

  const chooseFromQueue = (q) => {
    setMode('putaway')
    setProductId(String(q.product_id))
    setCount(q.qty ? String(q.qty) : '')
    setQueueId(q.id || null)
    setError('')
  }

  const save = async () => {
    setError('')
    const n = num(count)
    if (!item) { setError('Choose the item.'); return }
    if (!(n > 0)) { setError('Enter how many.'); return }
    if (!toBin) { setError('Choose the bin it’s going to.'); return }
    if (mode === 'putaway' && n > loose + 1e-9) {
      setError(loose > 0 ? `Only ${qty(loose)} aren’t in a bin yet.` : 'Everything on hand is already in a bin. To move it, choose “Move between bins”.')
      return
    }
    if (mode === 'move') {
      if (!fromBin) { setError('Choose the bin it’s coming from.'); return }
      if (String(fromBin.area_id) === String(toBin.area_id)) { setError('Choose two different bins.'); return }
      if (n > num(fromBin.qty) + 1e-9) { setError(`Only ${qty(fromBin.qty)} are in ${fromBin.path}.`); return }
    }
    setSaving(true)
    try {
      const base = { product_id: item.product_id, quantity: n, unit_cost: unitCost(item), to_location_id: Number(toLoc), to_area_text: toBin.path }
      if (mode === 'putaway') {
        await createStockMovement({ ...base, move_type: 'putaway', notes: 'Put away' })
        const queued = queue?.find((q) => q.id && q.id === queueId)
        if (queued && n >= queued.qty - 1e-9) await markPutawayDone(queued.id).catch(() => {})
        notify(`Put ${qty(n)} ${item.name || item.sku} in ${toBin.path}`)
      } else {
        await createStockMovement({ ...base, move_type: 'transfer', from_location_id: Number(fromBin.location_id), from_area_text: fromBin.path, notes: 'Moved between bins' })
        notify(`Moved ${qty(n)} ${item.name || item.sku} from ${fromBin.path} to ${toBin.path}`)
      }
      setCount('')
      setQueueId(null)
      setVersion((v) => v + 1)
    } catch (e) {
      setError(e.message || 'Could not save that.')
    } finally {
      setSaving(false)
    }
  }

  const waiting = (queue || []).filter((q) => q.product_id)

  return (
    <div className="ci-page">
      <BinNav title="Put away & move" meta="Put received goods into bins, or move stock from one bin to another." />

      {error && <ErrorBanner message={error} />}

      <div className="segmented" role="radiogroup" aria-label="What are you doing?">
        <button type="button" role="radio" aria-checked={mode === 'putaway'} className={mode === 'putaway' ? 'active' : ''} onClick={() => { setMode('putaway'); setError('') }}>
          Put away<small>Into a bin after receiving</small>
        </button>
        <button type="button" role="radio" aria-checked={mode === 'move'} className={mode === 'move' ? 'active' : ''} onClick={() => { setMode('move'); setQueueId(null); setError('') }}>
          Move between bins<small>From one bin to another</small>
        </button>
      </div>

      <div className="inv-bin-layout">
        <div className="inv-bin-form">
          <div className="ci-form-meta-card">
            <ItemPicker items={items} value={productId} onChange={(id) => { setProductId(id); setQueueId(null) }} />
            {item && (
              <p className="doc-hint">
                {qty(item.qty_on_hand)} on hand · {qty(inBins)} in bins{loose > 0 ? ` · ${qty(loose)} waiting to be put away` : ''}
              </p>
            )}
          </div>

          <div className="ci-form-meta-card">
            <div className="ci-form-meta-top two">
              {mode === 'move' && (
                <div className="inv-bin-field">
                  <label className="ci-form-field">
                    <span>From bin</span>
                    <select value={fromAreaId} onChange={(e) => setFromAreaId(e.target.value)} disabled={!item}>
                      <option value="">{item ? (holding.length ? 'Choose a bin…' : 'Not in any bin') : 'Choose the item first'}</option>
                      {holding.map((r) => <option key={r.area_id} value={r.area_id}>{r.path} · {r.location} · {qty(r.qty)}</option>)}
                    </select>
                  </label>
                  {item && (
                    <BinScan
                      areas={allAreas}
                      onPick={(a) => setFromAreaId(String(a.area_id))}
                      prefer={(a) => holding.some((r) => String(r.area_id) === String(a.area_id))}
                      validate={(a) => notABin(a) || (holding.some((r) => String(r.area_id) === String(a.area_id)) ? '' : `${item.name || item.sku} isn’t in ${a.path}.`)}
                      placeholder="Or scan the bin it’s in"
                    />
                  )}
                </div>
              )}
              {locations?.length > 1 && (
                <label className="ci-form-field">
                  <span>To location</span>
                  <select value={toLoc} onChange={(e) => { setToLoc(e.target.value); setToAreaId('') }}>
                    {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
                  </select>
                </label>
              )}
              <div className="inv-bin-field">
                <label className="ci-form-field">
                  <span>To bin</span>
                  <select value={toAreaId} onChange={(e) => setToAreaId(e.target.value)}>
                    <option value="">{toBins.length ? 'Choose a bin…' : 'No bins here yet'}</option>
                    {toBins.map((b) => <option key={b.area_id} value={b.area_id}>{binLabel(b)}{itemBins?.pick_path === b.path ? ' (pick bin)' : ''}</option>)}
                  </select>
                </label>
                <BinScan areas={allAreas} locationId={toLoc} onPick={scanTo} validate={notABin} placeholder="Or scan the bin it’s going to" />
              </div>
              <label className="ci-form-field">
                <span>How many{mode === 'move' && fromBin ? ` (${qty(fromBin.qty)} there)` : (mode === 'putaway' && item ? ` (${qty(loose)} to put away)` : '')}</span>
                <input value={count} onChange={(e) => setCount(e.target.value)} inputMode="decimal" placeholder="0" />
              </label>
            </div>
          </div>

          <div className="ci-form-action-bar">
            <div className="ci-form-action-left">
              <button type="button" className="ci-btn ci-btn-primary" disabled={saving || !item} onClick={save}>
                {mode === 'putaway' ? <PackagePlus size={14} /> : <ArrowLeftRight size={14} />}
                {saving ? 'Saving…' : (mode === 'putaway' ? 'Put away' : 'Move')}
              </button>
            </div>
          </div>
        </div>

        {mode === 'putaway' && (
          <section className="doc-card inv-bin-queue">
            <div className="doc-card-head">
              <h2>Waiting to be put away</h2>
              <span>{queue == null ? 'Loading…' : `${waiting.length} ${waiting.length === 1 ? 'item' : 'items'}`}</span>
            </div>
            {waiting.length ? (
              <ul className="sum-list inv-queue-list">
                {waiting.map((q, i) => (
                  <li key={q.id || `${q.product_id}-${i}`} className={String(q.product_id) === String(productId) ? 'active' : ''}>
                    <span className="sum-list-main">
                      {q.name || q.sku}
                      <span className="sum-muted"> · {qty(q.qty)}{q.when ? ` · ${fmtDay(q.when, { year: false })}` : ''}</span>
                    </span>
                    <span className="sum-list-side">
                      <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => chooseFromQueue(q)}>Put away</button>
                      {q.id && <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" title="Remove from this list" onClick={async () => { await markPutawayDone(q.id).catch(() => {}); setVersion((v) => v + 1) }}>Done</button>}
                    </span>
                  </li>
                ))}
              </ul>
            ) : <p className="doc-empty">{queue == null ? 'Loading…' : 'Nothing is waiting. Goods appear here after you receive them.'}</p>}
          </section>
        )}
      </div>
    </div>
  )
}
